import { useEffect, useState } from "react";
import { GrDialog, GrDialogProps, GrStack, GrTextField, GrTypography } from "@jnpmedi/grebob";
import { useQueryClient } from "@tanstack/react-query";
import { MvnMessage } from "@newLibs/utils";
import { UseBoolean } from "@newHooks/common/useBoolean";
import { notify } from "@newHooks/context/snackbar/notify";
import { useRenameFolderMutation } from "@newHooks/mutation/folder/renameFolder.mutation";
import { useRenameFolderFileMutation } from "@newHooks/mutation/folderFile/renameFolderFile.mutation";
import { FolderKey } from "@newHooks/query/folder/key";
import { DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";
import { DocumentSignCriteria } from "@maven-rest/docs/DocumentSign.dto";
import { BrowseEntryType, FolderCriteria } from "@maven-rest/docs/Folder.dto";
import { FolderFileCriteria } from "@maven-rest/docs/FolderFile.dto";

interface RenameResourceDialogProps {
  open: UseBoolean;
  renameResource: { resourceId: bigint; name: string; type: BrowseEntryType };
  size?: GrDialogProps["size"];
}

export function RenameResourceDialog({ open, renameResource, size = "xs" }: RenameResourceDialogProps) {
  const queryClient = useQueryClient();

  const [changedResourceName, setChangeResourceName] = useState<string>(renameResource.name);

  const nameMaxLength =
    renameResource.type === BrowseEntryType.Sign
      ? DocumentSignCriteria.ICF_TITLE_MAXLEN
      : BrowseEntryType.Folder
      ? FolderCriteria.FOLDER_NAME_MAXLEN
      : FolderFileCriteria.FOLDER_FILE_NAME_MAXLEN;

  const { mutate: renameFolderRequest, isLoading: isRenameFolderLoading } = useRenameFolderMutation(
    {
      folderId: renameResource.resourceId,
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [FolderKey.BrowseFolders] });

        notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_RENAME_SUCC), "SUCC");
      },
      onError: (error) => notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_RENAME_FAIL, { message: error.responseMessage }), "ERROR"),
    }
  );

  const { mutate: renameFolderFileRequest, isLoading: isRenameFolderFileLoading } = useRenameFolderFileMutation(
    {
      fileId: renameResource.resourceId,
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [FolderKey.BrowseFolders] });

        notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_RENAME_SUCC), "SUCC");
      },
      onError: (error) => notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_RENAME_FAIL, { message: error.responseMessage }), "ERROR"),
    }
  );

  useEffect(() => {
    if (open.value) setChangeResourceName(renameResource.name);
  }, [renameResource.name, open.value]);

  const handleConfirmRename = () => {
    if (isRenameFolderLoading || isRenameFolderFileLoading) return;

    if (!changedResourceName.trim()?.length || changedResourceName === renameResource.name) {
      return;
    }

    if (renameResource.type === BrowseEntryType.Folder) {
      renameFolderRequest(
        { folderId: renameResource.resourceId, name: changedResourceName },
        {
          onSuccess: () => {
            open.onFalse();
          },
        }
      );
    } else {
      renameFolderFileRequest(
        { fileId: renameResource.resourceId, name: changedResourceName },
        {
          onSuccess: () => {
            open.onFalse();
          },
        }
      );
    }
  };

  const handleEnterKey = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleConfirmRename();
    }
  };

  return (
    <GrDialog
      title={MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_RENAME_DIALOG_HEADER)}
      size={size}
      open={open.value}
      confirmButton={{
        children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_OK_NOUN),
        disabled: !changedResourceName.trim()?.length || changedResourceName === renameResource.name,
      }}
      cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CANCEL_NOUN) }}
      onConfirm={handleConfirmRename}
      isLoading={isRenameFolderLoading || isRenameFolderFileLoading}
      onClose={open.onFalse}
    >
      <GrStack flexDirection="column" gap={24}>
        <GrTypography type="Content.Body03" color="grey070">
          {MvnMessage(DOCS_FOLDER_LANG.DOCS_ARCHIVER_RENAME_DIALOG_DESCRIPTION)}
        </GrTypography>
        <GrTextField
          autoFocus
          type="text"
          required
          fullWidth
          label={{ text: "Name" }}
          value={changedResourceName}
          maxLength={nameMaxLength}
          placeholder="New name"
          onChange={(evt) => setChangeResourceName(evt.target.value)}
          onKeyDown={handleEnterKey}
        />
      </GrStack>
    </GrDialog>
  );
}
